import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { api } from "../api";
import type { CaseRecord, Contact, RecordFact } from "../types";
import type { LedgerDisposition } from "../lib/financialTotals";
import {
  buildLoadedLedgerState,
  buildReimbursementSummary,
  detectStaleOverrides,
  navTargetForRow,
  pairKey,
  relinkStatus,
  STATUS_DEFS,
  statusTooltip,
  TXN_STATUSES,
  UNTRIAGED_DEF,
  type CollectedContact,
  type ContactRow,
  type LedgerEdits,
  type LedgerRow,
  type ManualTbdItem,
  type RowMention,
  type RowSource,
  type StaleOverride,
  type StatusOverride,
  type TxnStatus,
} from "../lib/reimbursementSummary";
import { categoryFactKey } from "../lib/financialTotals";
import { exportReimbursementXlsx } from "../lib/exportXlsx";
import { Button } from "./ui/Button";
import SourcePanel from "./SourcePanel";

// Reimbursement ledger (Round 14): a tracker-shaped cost ledger the user
// finishes assembling via inline controls while viewing source facts. The
// table IS the worklist — Status / amount / merge / link are resolved per row.

// Per-case cache of the user's ledger edits. Clicking "view source" unmounts
// this view (tab → records); on Back it remounts and reloads from the server.
// Until/unless a write has fully landed, that reload would drop the just-made
// change — the "resolved row reappears unresolved" bug. The cache survives the
// unmount (module scope) and is overlaid on the server fetch so the user's
// latest intent is never lost mid-session. Cleared on full page reload, where
// the server is the source of truth.
const ledgerEditCache = new Map<string, LedgerEdits>();

// Inline source panel sizing. The panel lives to the RIGHT of the table; the
// divider is user-draggable within bounds. Width is a per-user preference
// (persisted), NOT per-row — it should not reset each time the panel reopens.
const PANEL_MIN_W = 360; // below this the source panel isn't usable
// Reserved table width = the max the panel may grow to is shellW - TABLE_MIN_W.
// This is the table's SOFT floor: past it the table scrolls horizontally
// (overflow-x on .rs-view in split mode). It is intentionally well below the
// table's no-scroll comfortable width (~620px): the content area (.main) is
// capped at ~1020px usable, so reserving 620 left only ~40px of drag travel and
// the divider appeared stuck. 480 gives a real range while the scroll backstop
// keeps the controls reachable; collapsing the left sidebar adds more room.
const TABLE_MIN_W = 480;
const PANEL_DEFAULT_W = 460;
const PANEL_NARROW_BP = 1024; // below this we switch to an overlay drawer
const PANEL_WIDTH_KEY = "casethread.financials.sourcePanelWidth";

// Max panel width for a given shell width — never below PANEL_MIN_W, so the
// clamp range is always non-empty (the divider is never pinned).
function maxPanelW(shellW: number): number {
  return Math.max(PANEL_MIN_W, shellW - TABLE_MIN_W);
}

function loadPanelWidth(): number {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    if (raw) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= PANEL_MIN_W) return n;
    }
  } catch {
    /* storage blocked — fall through to default */
  }
  return PANEL_DEFAULT_W;
}

function savePanelWidth(w: number): void {
  try {
    localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(w)));
  } catch {
    /* storage unavailable — width just resets next session */
  }
}

function clampWidth(w: number, max: number): number {
  return Math.max(PANEL_MIN_W, Math.min(w, Math.max(PANEL_MIN_W, max)));
}

interface Props {
  caseId: string;
  caseName: string;
  records: CaseRecord[];
  contacts: Contact[];
  onContactsChanged: (next: Contact[]) => void;
  refreshNonce: number;
  onNavigateToRecord: (recordId: string, attachmentId: string | null, rowKey: string) => void;
  focusRow: { key: string; n: number } | null;
  onFocusRowHandled: () => void;
}

function fmtCents(cents: number | null): string {
  if (cents === null) return "—";
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function ReimbursementSummaryView({
  caseId, caseName, records, contacts, onContactsChanged, refreshNonce,
  onNavigateToRecord, focusRow, onFocusRowHandled,
}: Props) {
  const [facts, setFacts] = useState<RecordFact[]>([]);
  const [bucketOverrides, setBucketOverrides] = useState<Map<string, LedgerDisposition>>(new Map());
  const [statusOverrides, setStatusOverrides] = useState<Map<string, StatusOverride>>(new Map());
  const [mergeLinks, setMergeLinks] = useState<Map<string, "merge" | "separate">>(new Map());
  const [manualTbd, setManualTbd] = useState<ManualTbdItem[]>([]);
  const [sources, setSources] = useState<Map<string, RowSource>>(new Map());
  const [collected, setCollected] = useState<CollectedContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  // Inline undo for a status change: the row key + the status it had before.
  const [undo, setUndo] = useState<{ key: string; prev: TxnStatus | null; applied: TxnStatus | null } | null>(null);
  // Surfaced (not swallowed) write failures, so a persistence problem is
  // visible instead of silently reverting.
  const [writeError, setWriteError] = useState<string | null>(null);
  // Load failures, distinguished from "loaded, genuinely empty" (audit H3):
  // factsFailed → can't build the ledger at all; savedDataFailed → facts
  // rendered but saved decisions/sources couldn't load (don't pass off an
  // untriaged-looking ledger as the truth). Retry bumps loadNonce.
  const [factsFailed, setFactsFailed] = useState(false);
  const [savedDataFailed, setSavedDataFailed] = useState(false);
  const [loadNonce, setLoadNonce] = useState(0);

  // Inline source panel: which row's source is shown beside the table. The
  // table never unmounts (no tab switch), so moving down the queue just
  // re-points this key and the panel re-resolves in place.
  const [activeRowKey, setActiveRowKey] = useState<string | null>(null);
  // Which cost groups are expanded to show their mentions, and (when a specific
  // mention's source is open) which mention the panel is focused on.
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const [activeMentionKey, setActiveMentionKey] = useState<string | null>(null);
  const toggleExpand = useCallback((key: string) => {
    setExpandedKeys((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const [panelWidth, setPanelWidth] = useState<number>(loadPanelWidth);
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth < PANEL_NARROW_BP,
  );
  // Live width of the split container — drives the max-panel clamp. Tracked
  // reactively (ResizeObserver) so the panel re-fits when the window resizes
  // OR the left sidebar collapses (a grid change with no window resize event).
  const [shellWidth, setShellWidth] = useState(0);
  const shellRef = useRef<HTMLDivElement | null>(null);

  // Track the narrow breakpoint → below it the panel is an overlay drawer and
  // the resizable split (and divider) do not apply.
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${PANEL_NARROW_BP - 1}px)`);
    const onChange = () => setIsNarrow(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Observe the shell's width once the view has rendered (keyed on loading so
  // the ref is attached). clientWidth reflects .main's max-width cap + padding,
  // so the clamp uses the true available space, not window.innerWidth.
  useEffect(() => {
    if (loading) return;
    const el = shellRef.current;
    if (!el) return;
    const measure = () => setShellWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  // Drag the divider: the panel is on the right, so dragging left widens it.
  // Bounded so the panel stays >= PANEL_MIN_W and the table keeps >= TABLE_MIN_W
  // (it scrolls horizontally past that). Persisted on pointer-up (per-user).
  const startResize = useCallback((e: ReactPointerEvent) => {
    e.preventDefault();
    // Route all subsequent pointer events to the divider so a drag that crosses
    // the inline PDF iframe isn't swallowed by it.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* unsupported */ }
    const startX = e.clientX;
    const startW = panelWidth;
    const shellW = shellRef.current?.clientWidth ?? window.innerWidth;
    const maxW = maxPanelW(shellW);
    let latest = startW;
    const onMove = (ev: PointerEvent) => {
      latest = clampWidth(startW - (ev.clientX - startX), maxW);
      setPanelWidth(latest);
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      document.body.classList.remove("rs-resizing");
      savePanelWidth(latest);
    };
    document.body.classList.add("rs-resizing");
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }, [panelWidth]);

  // Keyboard nudge for the divider (accessibility + fine adjustment).
  const nudgeDivider = useCallback((e: ReactKeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const shellW = shellRef.current?.clientWidth ?? window.innerWidth;
    const maxW = maxPanelW(shellW);
    setPanelWidth((w) => {
      const next = clampWidth(w + (e.key === "ArrowLeft" ? 24 : -24), maxW);
      savePanelWidth(next);
      return next;
    });
  }, []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setFactsFailed(false);
    setSavedDataFailed(false);
    // Settle each store independently so one transient failure never masquerades
    // as empty data; the pure controller does the merge + severity split.
    const settle = <T,>(p: Promise<T>) =>
      p.then((value) => ({ ok: true, value })).catch(() => ({ ok: false as const }));
    Promise.all([
      settle(api.getCaseFacts(caseId).then((r) => r.facts)),
      settle(api.getAmountCategories(caseId).then((r) => r.overrides)),
      settle(api.getMergeLinks(caseId).then((r) => r.links)),
      settle(api.getTbdItems(caseId).then((r) => r.items)),
      settle(api.getSummarySources(caseId)),
    ]).then(([facts, overrides, links, tbd, sources]) => {
      if (!live) return;
      const s = buildLoadedLedgerState(
        { facts, overrides, links, tbd, sources },
        ledgerEditCache.get(caseId),
      );
      if (s.factsFailed) {
        setFactsFailed(true);
        setLoading(false);
        return;
      }
      setFacts(s.facts);
      setSavedDataFailed(s.savedDataFailed);
      setBucketOverrides(s.bucketOverrides);
      setStatusOverrides(s.statusOverrides);
      setMergeLinks(s.mergeLinks);
      setManualTbd(s.manualTbd);
      setSources(s.sources);
      setCollected(s.collected);
      setLoading(false);
    });
    return () => { live = false; };
  }, [caseId, refreshNonce, loadNonce]);

  // Keep the per-case edit cache in sync with the live editable state so the
  // user's changes survive the unmount when they jump to a source and back.
  useEffect(() => {
    if (loading) return;
    ledgerEditCache.set(caseId, { bucketOverrides, statusOverrides, mergeLinks, manualTbd });
  }, [caseId, loading, bucketOverrides, statusOverrides, mergeLinks, manualTbd]);

  const summary = useMemo(
    () => buildReimbursementSummary({
      caseName, records, facts, bucketOverrides, statusOverrides, mergeLinks,
      manualTbd, sources, collectedContacts: collected, manualContacts: contacts,
      todayIso: new Date().toISOString().slice(0, 10),
    }),
    [caseName, records, facts, bucketOverrides, statusOverrides, mergeLinks, manualTbd, sources, collected, contacts],
  );

  // Saved decisions whose content key no longer matches any current fact
  // (re-extraction reworded a quote / shifted an amount). Surfaced, never
  // silently dropped or reapplied (audit H1).
  const stale = useMemo(
    () => detectStaleOverrides(facts, { bucketOverrides, statusOverrides, mergeLinks, manualTbd }),
    [facts, bucketOverrides, statusOverrides, mergeLinks, manualTbd],
  );

  // Every ledger row across all sections, keyed for the source panel's
  // in-place re-resolution as the user moves down the queue.
  const rowsByKey = useMemo(() => {
    const m = new Map<string, LedgerRow>();
    for (const r of [
      ...summary.payments.rows,
      ...summary.outOfPocket.rows,
      ...summary.tbd,
      ...summary.needsReview,
      ...summary.resolvedNoCost,
      ...summary.documentedUncosted,
    ]) {
      m.set(r.key, r);
    }
    return m;
  }, [summary]);

  // Verbatim quote(s) backing a row (>1 when facts merged into it). Looked up
  // from the same content key the ledger uses — no AI, no extra fetch.
  const quotesFor = useCallback(
    (key: string): string[] =>
      facts.filter((f) => categoryFactKey(f) === key).map((f) => f.verbatim_quote),
    [facts],
  );

  const activeRow = activeRowKey ? rowsByKey.get(activeRowKey) ?? null : null;

  // If the active row vanishes (re-extraction reworded its key, merge folded
  // it away), close the panel rather than leaving a dangling divider.
  useEffect(() => {
    if (activeRowKey && !rowsByKey.has(activeRowKey)) setActiveRowKey(null);
  }, [activeRowKey, rowsByKey]);

  // --- inline actions (optimistic) ---------------------------------------
  const patchStatusOverride = useCallback((key: string, patch: Partial<StatusOverride>) => {
    setStatusOverrides((m) => {
      const next = new Map(m);
      const cur = next.get(key) ?? { status: null, amountCentsOverride: null };
      next.set(key, { ...cur, ...patch });
      return next;
    });
  }, []);

  const setStatus = useCallback(async (key: string, status: TxnStatus | null) => {
    patchStatusOverride(key, { status });
    try { await api.setAmountOverride(caseId, key, { status }); } catch (e) { setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || "")); }
  }, [caseId, patchStatusOverride]);

  // User changed a row's status via the dropdown: apply it AND remember the
  // prior value so the inline "Undo" can revert (reusing the same override
  // persistence — reverting just writes the prior status / clears it).
  const changeStatus = useCallback((row: LedgerRow, status: TxnStatus | null) => {
    setUndo({ key: row.key, prev: row.status, applied: status });
    void setStatus(row.key, status);
  }, [setStatus]);

  const undoStatus = useCallback(() => {
    if (!undo) return;
    void setStatus(undo.key, undo.prev);
    setUndo(null);
  }, [undo, setStatus]);

  // H1 recovery — clear a stale decision (no auto-delete; user-initiated).
  const dismissStale = useCallback(async (s: StaleOverride) => {
    try {
      if (s.kind === "status") {
        setStatusOverrides((m) => { const n = new Map(m); n.delete(s.key); return n; });
        setBucketOverrides((m) => { const n = new Map(m); n.delete(s.key); return n; });
        await api.setAmountOverride(caseId, s.key, { status: null, bucket: null, amount_cents_override: null });
      } else if (s.kind === "merge") {
        setMergeLinks((m) => new Map(m).set(s.key, "separate"));
        await api.setMergeLink(caseId, s.key, "separate");
      } else {
        // graduation: detach the resolving fact from the manual TBD item.
        setManualTbd((items) => items.map((it) => it.key === s.key ? { ...it, resolvedFactKey: null } : it));
        const item = manualTbd.find((it) => it.key === s.key);
        if (item) await api.putTbdItem(caseId, { key: item.key, description: item.description, party: item.party, status: item.status, amount_cents: item.amountCents, resolved_fact_key: null });
      }
    } catch (e) {
      setWriteError("Couldn't dismiss — " + ((e as Error).message || ""));
    }
  }, [caseId, manualTbd]);

  // H1 recovery — re-link a stale status decision onto a current (untriaged)
  // row: write the remembered status/amount under that row's key, then clear
  // the stale entry.
  const relinkStale = useCallback(async (s: StaleOverride, targetKey: string) => {
    const status = relinkStatus(s);
    if (!status) return;
    patchStatusOverride(targetKey, { status, ...(s.amountOverride != null ? { amountCentsOverride: s.amountOverride } : {}) });
    try {
      await api.setAmountOverride(caseId, targetKey, { status, ...(s.amountOverride != null ? { amount_cents_override: s.amountOverride } : {}) });
    } catch (e) {
      setWriteError("Couldn't re-link — " + ((e as Error).message || ""));
    }
    await dismissStale(s);
  }, [caseId, patchStatusOverride, dismissStale]);

  // Auto-dismiss the undo affordance after a few seconds.
  useEffect(() => {
    if (!undo) return;
    const t = window.setTimeout(() => setUndo(null), 12000);
    return () => window.clearTimeout(t);
  }, [undo]);

  const editAmount = useCallback(async (row: LedgerRow) => {
    const cur = row.amountCents != null ? (row.amountCents / 100).toFixed(2) : "";
    const input = window.prompt(`Amount for "${row.description.slice(0, 60)}" (dollars):`, cur);
    if (input == null) return;
    const dollars = Number(input.replace(/[^0-9.\-]/g, ""));
    if (!Number.isFinite(dollars)) return;
    const cents = Math.round(dollars * 100);
    patchStatusOverride(row.key, { amountCentsOverride: cents });
    try { await api.setAmountOverride(caseId, row.key, { amount_cents_override: cents }); } catch (e) { setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || "")); }
  }, [caseId, patchStatusOverride]);

  const decideMerge = useCallback(async (a: string, b: string, decision: "merge" | "separate") => {
    const pk = pairKey(a, b);
    setMergeLinks((m) => new Map(m).set(pk, decision));
    try { await api.setMergeLink(caseId, pk, decision); } catch (e) { setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || "")); }
  }, [caseId]);

  const linkGraduation = useCallback(async (tbdKey: string, factKey: string, link: boolean) => {
    if (link) {
      setManualTbd((items) => items.map((it) => it.key === tbdKey ? { ...it, resolvedFactKey: factKey, status: it.status ?? "Paid" } : it));
      const item = manualTbd.find((it) => it.key === tbdKey);
      if (item) {
        try { await api.putTbdItem(caseId, { key: item.key, description: item.description, party: item.party, status: item.status ?? "Paid", amount_cents: item.amountCents, resolved_fact_key: factKey }); } catch (e) { setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || "")); }
      }
    } else {
      await decideMerge(tbdKey, factKey, "separate");
    }
  }, [caseId, manualTbd, decideMerge]);

  const addTbd = useCallback(async () => {
    const description = window.prompt("Describe the cost (e.g. 'Tow from impound'):", "");
    if (!description || !description.trim()) return;
    const party = window.prompt("Paid to / party (optional):", "") || null;
    const key = `manual:${description.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}:${Date.now()}`;
    const item: ManualTbdItem = { key, description: description.trim(), party, status: "Outstanding", amountCents: null, resolvedFactKey: null };
    setManualTbd((items) => [...items, item]);
    try { await api.putTbdItem(caseId, { key, description: item.description, party, status: "Outstanding" }); } catch (e) { setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || "")); }
  }, [caseId]);

  const removeTbd = useCallback(async (key: string) => {
    setManualTbd((items) => items.filter((it) => it.key !== key));
    try { await api.deleteTbdItem(caseId, key); } catch (e) { setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || "")); }
  }, [caseId]);

  const promoteContact = useCallback(async (row: ContactRow) => {
    // The user assigns the party — we never guess whose contact it is.
    const name = window.prompt(`Add ${row.value} to contacts. Name / party:`, "");
    if (name == null) return;
    try {
      const { contact } = await api.createContact(caseId, {
        name: name.trim() || row.value, role: null,
        email: row.type === "email" ? row.value : null,
        phone: row.type === "phone" ? row.value : null,
        notes: "Added from Financials (verbatim from source)",
      });
      onContactsChanged([...contacts, contact]);
    } catch { /* unchanged flag remains on failure */ }
  }, [caseId, contacts, onContactsChanged]);

  // --- source round-trip + return precision (reused pattern) -------------
  const jump = useCallback((row: { key: string; source: RowSource | null }) => {
    const t = navTargetForRow(row);
    if (!t) return;
    onNavigateToRecord(t.recordId, t.attachmentId, row.key);
  }, [onNavigateToRecord]);

  // The in-table path: open/re-point the inline source panel for this row
  // (the table stays put). Reuse the existing flash to mark the active row.
  const openSource = useCallback((row: { key: string; source: RowSource | null }) => {
    if (!row.source) return;
    setActiveMentionKey(null); // group-level view, not a single mention
    setActiveRowKey(row.key);
    setFlashKey(row.key);
  }, []);

  // Open the source panel focused on ONE mention of a group (its record).
  const openMentionSource = useCallback((row: LedgerRow, m: RowMention) => {
    if (!m.source) return;
    setActiveMentionKey(m.key);
    setActiveRowKey(row.key);
    setFlashKey(row.key);
  }, []);

  // EXCLUDE a wrongly-grouped mention → cut it from every sibling (cannot-link
  // to each), so it falls out as its own row. It stays in the ledger to triage;
  // never silently dropped from the total.
  const excludeMention = useCallback(async (row: LedgerRow, m: RowMention) => {
    const others = row.mentions.filter((o) => o.key !== m.key);
    if (others.length === 0) return;
    setMergeLinks((prev) => {
      const next = new Map(prev);
      for (const o of others) next.set(pairKey(m.key, o.key), "separate");
      return next;
    });
    try {
      for (const o of others) await api.setMergeLink(caseId, pairKey(m.key, o.key), "separate");
    } catch (e) {
      setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || ""));
    }
  }, [caseId]);

  // Cross-stage: mark THIS mention's amount as the actual/Paid figure for the
  // group. Only the chosen amount counts; the others stay shown but superseded.
  const markActual = useCallback(async (row: LedgerRow, m: RowMention) => {
    patchStatusOverride(row.key, { amountCentsOverride: m.amountCents });
    try {
      await api.setAmountOverride(caseId, row.key, { amount_cents_override: m.amountCents });
    } catch (e) {
      setWriteError("Couldn't save your change — it's kept for now but may not persist on reload. " + ((e as Error).message || ""));
    }
  }, [caseId, patchStatusOverride]);

  useEffect(() => {
    if (!focusRow) return;
    const key = focusRow.key;
    let frames = 0, raf = 0, timeoutId = 0;
    const attempt = () => {
      const el = document.getElementById(`sumrow-${key}`);
      if (!el) return false;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setFlashKey(key);
      onFocusRowHandled();
      return true;
    };
    const tick = () => {
      if (attempt()) return;
      if (frames++ < 30) raf = requestAnimationFrame(tick);
      else timeoutId = window.setTimeout(() => { if (!attempt()) onFocusRowHandled(); }, 500);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); if (timeoutId) window.clearTimeout(timeoutId); };
  }, [focusRow, onFocusRowHandled]);

  useEffect(() => {
    if (!flashKey) return;
    const t = window.setTimeout(() => setFlashKey(null), 2100);
    return () => window.clearTimeout(t);
  }, [flashKey]);

  if (loading) return <div className="rs-loading">Loading ledger…</div>;

  // H3: facts couldn't load — show a clear error + Retry, never an empty ledger.
  if (factsFailed) {
    return (
      <div className="rs-loaderror" role="alert">
        <p>Couldn't load this case's financial data.</p>
        <Button variant="secondary" size="sm" onClick={() => setLoadNonce((n) => n + 1)}>
          Retry
        </Button>
      </div>
    );
  }

  // Untriaged rows the user can re-link a stale decision onto.
  const relinkTargets = summary.needsReview;
  const c = summary.completeness;
  const parts: string[] = [];
  if (c.needStatus) parts.push(`${c.needStatus} need a status`);
  if (c.possibleDups) parts.push(`${c.possibleDups} possible duplicate${c.possibleDups === 1 ? "" : "s"}`);
  if (c.graduationsOffered) parts.push(`${c.graduationsOffered} graduation${c.graduationsOffered === 1 ? "" : "s"} to confirm`);
  if (c.tbdUnvalued) parts.push(`${c.tbdUnvalued} TBD without an amount`);

  const renderRow = (row: LedgerRow, opts: { section: "pay" | "oop" | "tbd" | "review" | "resolved" | "uncosted" }) => {
    const grad = opts.section === "tbd" ? summary.graduationOffers.find((g) => g.tbdKey === row.key) : undefined;
    const expanded = expandedKeys.has(row.key);
    const recordCount = new Set(row.mentions.map((m) => m.recordId)).size;
    // Does any of this row's sources resolve to a viewable document (PDF/image)?
    // If so, the source panel's "Extracted text ↔ Original document" toggle will
    // appear; if not, it's record body text only. Uses the source already
    // resolved (row.source / row.sources → attachmentId present).
    const hasDoc =
      !!row.source?.attachmentId || row.sources.some((s) => !!s?.attachmentId);
    return (
      <Fragment key={row.key}>
      <tr id={`sumrow-${row.key}`} className={`rs-row rs-state-${row.state}${flashKey === row.key ? " rs-flash" : ""}${activeRowKey === row.key ? " rs-active" : ""}`}>
        <td>{row.date ?? "—"}</td>
        <td>
          {hasDoc && (
            <span
              className="rs-src-doc"
              title="Source includes a viewable document (PDF/image) — the source panel shows the original"
              aria-label="has a viewable document"
            >
              📎
            </span>
          )}
          {row.description}
          {row.provenance === "manual" && <span className="rs-tag rs-tag-manual" title="Manually entered — no verbatim source">manual</span>}
          {row.amountEdited && <span className="rs-tag rs-tag-edited" title="Amount entered/edited by you">edited</span>}
          {row.mergedCount > 1 && (
            <button
              type="button"
              className="rs-link rs-mentions-toggle"
              aria-expanded={expanded}
              onClick={() => toggleExpand(row.key)}
              title="Show the records this cost was assembled from"
            >
              {expanded ? "▾" : "▸"} {row.mergedCount} mention{row.mergedCount === 1 ? "" : "s"} across {recordCount} record{recordCount === 1 ? "" : "s"}
            </button>
          )}
          {row.possiblySameAsKey && (
            <span className="rs-merge">
              possibly same — <button className="rs-link" onClick={() => decideMerge(row.key, row.possiblySameAsKey!, "merge")}>Merge</button> / <button className="rs-link" onClick={() => decideMerge(row.key, row.possiblySameAsKey!, "separate")}>Keep separate</button>
            </span>
          )}
          {grad && (
            <span className="rs-merge">
              new {fmtCents(summary.payments.rows.concat(summary.outOfPocket.rows, summary.needsReview).find((r) => r.key === grad.factKey)?.amountCents ?? null)} — is this it? <button className="rs-link" onClick={() => linkGraduation(row.key, grad.factKey, true)}>Link</button> / <button className="rs-link" onClick={() => linkGraduation(row.key, grad.factKey, false)}>Keep separate</button>
            </span>
          )}
        </td>
        <td>{row.party ?? ""}</td>
        <td className="rs-amt">
          <span className={opts.section === "resolved" ? "rs-amt-waived" : undefined}>{fmtCents(row.amountCents)}</span>
          {opts.section === "resolved" && <span className="rs-tag" title="Resolved to $0 — no cost to you"> → $0</span>}
          <button className="rs-link rs-edit" title="Edit amount" onClick={() => editAmount(row)}>✎</button>
        </td>
        <td>
          {/* title on the control = the CURRENT status's definition; title on
              each option = that option's. All strings come from STATUS_DEFS /
              statusTooltip so the dropdown doubles as a routing guide. */}
          <select
            className="rs-status"
            value={row.status ?? ""}
            title={statusTooltip(row.status)}
            onChange={(e) => changeStatus(row, (e.target.value || null) as TxnStatus | null)}
          >
            <option value="" title={statusTooltip(null)}>{UNTRIAGED_DEF.label}</option>
            {TXN_STATUSES.map((s) => (
              <option key={s} value={s} title={statusTooltip(s)}>{STATUS_DEFS[s].label}</option>
            ))}
          </select>
        </td>
        <td>
          {row.source ? (
            <button
              className={`rs-link${activeRowKey === row.key ? " rs-link-active" : ""}`}
              aria-pressed={activeRowKey === row.key}
              onClick={() => openSource(row)}
            >
              view source
            </button>
          ) : opts.section === "tbd" ? <button className="rs-link" onClick={() => removeTbd(row.key)}>remove</button> : null}
        </td>
      </tr>
      {expanded && row.mentions.map((m) => (
        <tr key={`${row.key}::${m.key}`} className={`rs-mention-row${activeMentionKey === m.key ? " rs-active" : ""}`}>
          <td>{m.date ?? "—"}</td>
          <td className="rs-mention-desc">
            <span className="rs-mention-arrow" aria-hidden>↳</span> {m.description}
            {m.superseded && <span className="rs-tag" title="A different amount than the counted actual — kept as history, not counted">superseded</span>}
          </td>
          <td></td>
          <td className="rs-amt">
            <span className={m.superseded ? "rs-amt-waived" : undefined}>{fmtCents(m.amountCents)}</span>
          </td>
          <td>
            {row.mergedCount > 1 && m.superseded && (
              <button className="rs-link" title="Count THIS amount as the actual for this charge" onClick={() => markActual(row, m)}>Mark actual</button>
            )}
          </td>
          <td className="rs-mention-actions">
            {m.source && (
              <button
                className={`rs-link${activeMentionKey === m.key ? " rs-link-active" : ""}`}
                aria-pressed={activeMentionKey === m.key}
                onClick={() => openMentionSource(row, m)}
              >
                view source
              </button>
            )}
            {row.mentions.length > 1 && (
              <button className="rs-link" title="Not part of this charge — split into its own row" onClick={() => excludeMention(row, m)}>Exclude</button>
            )}
          </td>
        </tr>
      ))}
      </Fragment>
    );
  };

  const Section = ({ title, rows, subtotal, section }: { title: string; rows: LedgerRow[]; subtotal?: number; section: "pay" | "oop" | "tbd" | "review" | "resolved" | "uncosted" }) => (
    <section className="rs-section">
      <h3>{title}</h3>
      {rows.length === 0 ? <p className="rs-empty">None.</p> : (
        <table className="rs-table">
          <thead><tr><th>Date</th><th>Description</th><th>Paid To</th><th>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>{rows.map((r) => renderRow(r, { section }))}</tbody>
        </table>
      )}
      {subtotal !== undefined && (
        <div className="rs-subtotal"><strong>Subtotal: {fmtCents(subtotal)}</strong></div>
      )}
    </section>
  );

  // Clamp the persisted width to the live shell (a width saved on a wide screen
  // must not crush the table when reopened narrow). Until the shell is measured
  // (shellWidth 0) keep the raw width to avoid a first-paint jump.
  const effPanelWidth = shellWidth > 0 ? clampWidth(panelWidth, maxPanelW(shellWidth)) : panelWidth;

  // When a single mention's source is open, focus the panel on THAT mention's
  // record + quote; otherwise show the group's primary source + all quotes.
  const activeMention =
    activeRow && activeMentionKey
      ? activeRow.mentions.find((m) => m.key === activeMentionKey) ?? null
      : null;
  const panelRow = activeMention && activeRow
    ? { ...activeRow, source: activeMention.source, dateRaw: activeMention.dateRaw, description: activeMention.description }
    : activeRow;
  const panelQuotes = activeMention ? [activeMention.quote] : activeRow ? quotesFor(activeRow.key) : [];
  const panelKey = activeMention ? `${activeRow!.key}::${activeMention.key}` : activeRow?.key;
  const closePanel = () => { setActiveRowKey(null); setActiveMentionKey(null); };

  return (
    <div
      className="rs-shell"
      ref={shellRef}
      data-panel={activeRow ? (isNarrow ? "drawer" : "split") : "closed"}
    >
      <div className="rs-view">
      <div className="rs-head">
        <div className={`rs-completeness${c.total === 0 ? " rs-complete" : ""}`} role="status">
          {c.total === 0 ? "✓ Ledger complete — every row is placed and sourced." : `${c.total} to resolve: ${parts.join(" · ")}.`}
        </div>
        <Button variant="secondary" size="sm" onClick={() => exportReimbursementXlsx(summary)}>Export to Excel</Button>
      </div>

      {savedDataFailed && (
        <div className="rs-banner rs-banner-warn" role="alert">
          <span>Couldn't load your saved decisions / sources — the ledger may look less triaged than it is.</span>
          <button type="button" className="rs-link" onClick={() => setLoadNonce((n) => n + 1)}>Retry</button>
        </div>
      )}

      {stale.length > 0 && (
        <div className="rs-banner rs-banner-stale" role="alert">
          <div className="rs-banner-title">
            {stale.length} saved decision{stale.length === 1 ? "" : "s"} no longer match the current records — review
          </div>
          <ul className="rs-stale-list">
            {stale.map((s) => (
              <li key={`${s.kind}:${s.key}`} className="rs-stale-item">
                <span className="rs-stale-detail">
                  was: {s.kind === "merge" ? "merge" : s.kind === "graduation" ? `TBD “${s.description ?? ""}”` : (s.status ?? s.bucket ?? "decision")}
                  {s.amountCents != null ? ` · ${fmtCents(s.amountCents)}` : ""}
                  {s.quoteHead ? ` · “${s.quoteHead.slice(0, 48)}…”` : ""}
                </span>
                <span className="rs-stale-actions">
                  {s.kind === "status" && relinkTargets.length > 0 && (
                    <select
                      className="rs-status"
                      defaultValue=""
                      onChange={(e) => { if (e.target.value) void relinkStale(s, e.target.value); }}
                    >
                      <option value="">Re-link to…</option>
                      {relinkTargets.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.description.slice(0, 40)} · {fmtCents(r.amountCents)}
                        </option>
                      ))}
                    </select>
                  )}
                  <button type="button" className="rs-link" onClick={() => void dismissStale(s)}>Dismiss</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {undo && (
        <div className="rs-undo" role="status">
          <span>Status set to {undo.applied ?? "Untriaged"}.</span>
          <button type="button" className="rs-link" onClick={undoStatus}>Undo</button>
        </div>
      )}
      {writeError && (
        <div className="rs-undo rs-write-error" role="alert">
          <span>⚠ {writeError}</span>
          <button type="button" className="rs-link" onClick={() => setWriteError(null)}>Dismiss</button>
        </div>
      )}

      <section className="rs-section">
        <h3>Claim</h3>
        <dl className="rs-header-grid">
          <dt>Case</dt><dd>{summary.header.caseName}</dd>
          <dt>Records</dt><dd>{summary.header.recordCount}</dd>
          <dt>First activity</dt><dd>{summary.header.firstDate?.slice(0, 10) ?? "—"}</dd>
          <dt>Last activity</dt><dd>{summary.header.lastDate?.slice(0, 10) ?? "—"}</dd>
        </dl>
      </section>

      <Section title="Payments received" rows={summary.payments.rows} subtotal={summary.payments.subtotalCents} section="pay" />
      <Section title="Out-of-pocket costs" rows={summary.outOfPocket.rows} subtotal={summary.outOfPocket.subtotalCents} section="oop" />

      {/* Placed RIGHT BELOW the cost total so the understatement is legible at a
          glance — these confirmed items carry no price yet, so the headline
          figure above undercounts the true cost. Distinct from TBD/Accruing. */}
      {summary.documentedUncosted.length > 0 && (
        <section className="rs-section rs-uncosted">
          <h3>⚠ Documented but uncosted — excluded from current total</h3>
          <p className="rs-scaffold-note">
            Confirmed, documented items with no price yet (deliberately absent from the estimate). They are
            <strong> not counted above</strong>, so the headline cost is <strong>understated</strong> until they’re priced.
            Unlike Accruing items, there’s no running meter — these are a present gap, not a forward liability.
          </p>
          <table className="rs-table">
            <thead><tr><th>Date</th><th>Description</th><th>Paid To</th><th>Amount</th><th>Status</th><th></th></tr></thead>
            <tbody>{summary.documentedUncosted.map((r) => renderRow(r, { section: "uncosted" }))}</tbody>
          </table>
        </section>
      )}

      <section className="rs-section">
        <div className="rs-section-head">
          <h3>Costs to be determined (TBD)</h3>
          <button className="rs-add" onClick={addTbd}>+ Add TBD item</button>
        </div>
        {summary.tbd.length === 0 ? <p className="rs-empty">None.</p> : (
          <table className="rs-table">
            <thead><tr><th>Date</th><th>Description</th><th>Paid To</th><th>Amount</th><th>Status</th><th></th></tr></thead>
            <tbody>{summary.tbd.map((r) => renderRow(r, { section: "tbd" }))}</tbody>
          </table>
        )}
      </section>

      {summary.needsReview.length > 0 && (
        <Section title="Needs review (estimates / quotes / untriaged) — not counted" rows={summary.needsReview} section="review" />
      )}

      {summary.resolvedNoCost.length > 0 && (
        <section className="rs-section rs-resolved">
          <h3>Resolved / no cost — documented, but no cost to me</h3>
          <p className="rs-scaffold-note">Real charges that resolved to $0 (e.g. waived). Kept for the claim narrative; not counted in any subtotal.</p>
          <table className="rs-table">
            <thead><tr><th>Date</th><th>Description</th><th>Paid To</th><th>Documented amount</th><th>Status</th><th></th></tr></thead>
            <tbody>{summary.resolvedNoCost.map((r) => renderRow(r, { section: "resolved" }))}</tbody>
          </table>
          <div className="rs-subtotal"><span className="rs-subtotal-suppressed">Cost to me: $0.00 (waived / no cost)</span></div>
        </section>
      )}

      <section className="rs-section">
        <h3>Contacts</h3>
        {summary.contacts.length === 0 ? <p className="rs-empty">No phone/email found in sources.</p> : (
          <table className="rs-table"><tbody>
            {summary.contacts.map((row) => (
              <tr key={row.key} id={`sumrow-${row.key}`} className={flashKey === row.key ? "rs-flash" : ""}>
                <td>{row.type === "email" ? "✉" : "☎"} <strong>{row.value}</strong></td>
                <td>{row.source && <button className="rs-link" onClick={() => jump(row)}>view source ↗</button>}</td>
                <td>{row.promoted
                  ? <span className="rs-empty">in contacts</span>
                  : <button className="rs-link" onClick={() => promoteContact(row)}>Add to contacts</button>}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </section>

      <section className="rs-section rs-strategy">
        <h3>Strategic notes</h3>
        <p className="rs-scaffold-note">Yours to fill — never auto-populated.</p>
        {["Total-loss scenario / projected payout", "Demand / amount sought", "Supporting argument", "Next steps"].map((label) => (
          <div key={label} className="rs-scaffold-field"><label>{label}</label><div className="rs-scaffold-blank" aria-hidden /></div>
        ))}
      </section>
      </div>

      {activeRow && !isNarrow && (
        <div
          className="rs-divider"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize source panel"
          aria-valuenow={Math.round(effPanelWidth)}
          aria-valuemin={PANEL_MIN_W}
          aria-valuemax={Math.round(maxPanelW(shellWidth || effPanelWidth + TABLE_MIN_W))}
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={nudgeDivider}
          title="Drag to resize"
        >
          <span className="rs-divider-grip" aria-hidden />
        </div>
      )}

      {activeRow && (
        isNarrow ? (
          <>
            <div className="rs-panel-scrim" onClick={closePanel} />
            <div className="rs-panel rs-panel-drawer">
              <SourcePanel
                key={panelKey}
                caseId={caseId}
                records={records}
                row={panelRow!}
                quotes={panelQuotes}
                onClose={closePanel}
                onOpenFullRecord={() => jump(panelRow!)}
              />
            </div>
          </>
        ) : (
          <div
            className="rs-panel rs-panel-split"
            style={{ flex: `0 0 ${effPanelWidth}px`, width: effPanelWidth }}
          >
            <SourcePanel
              key={panelKey}
              caseId={caseId}
              records={records}
              row={panelRow!}
              quotes={panelQuotes}
              onClose={closePanel}
              onOpenFullRecord={() => jump(panelRow!)}
            />
          </div>
        )
      )}
    </div>
  );
}
